import { getWidgets } from '@libs/network-utils';
import {
    ActionSystemActionType,
    TPayloadWidget,
    TSystemActions,
    ActionTypeEnum,
    TCommonData,
    TWAction,
    TWidget,
    TWInternalId,
    TRequestUri,
    TInitPayerOptions,
} from '@libs/types';
import { addMutableWidgets, updateBottomSheetsWidgetsMap, updateInternalIdWidgets, updateModalWindowsMap } from './features/scenario.slice';
import { fetchJsonAsync, updateScenario } from './features/utils/sharedActions';
import { AppDispatchType } from './hooks';
import { getWidgetByActionWidgetId } from './widgets';

const SYSTEM_ACTIONS = 'systemActions';
const CURRENT_PAGE_WIDGETS = 'currentPageWidgets';
const REQUEST_URI = 'requestUri';

export const setEnvironmentRequestUri = (requestUri?: TRequestUri) => {
    if (requestUri) {
        sessionStorage.setItem(REQUEST_URI, JSON.stringify(requestUri));
    }
};
function updateWidgetInWidgets(widgets: Array<TPayloadWidget<TCommonData>>, updatedWidget: TPayloadWidget<TCommonData>): Array<TPayloadWidget<TCommonData>> {
    return widgets.map((widget) => {
        if (widget?.uid === updatedWidget?.uid) {
            return updatedWidget;
        }
        if (widget.widgets) {
            return {
                ...widget,
                widgets: updateWidgetInWidgets(widget.widgets, updatedWidget),
            } as TPayloadWidget<TCommonData>;
        }
        return widget;
    });
}

export const managePs3PlayerCookies = (): Record<string, string> => {
    const prefix = 'data-ps3-player-';
    const cookies: Record<string, string> = {};

    const allCookies = document.cookie.split(';');

    for (const cookie of allCookies) {
        const trimmedCookie = cookie.trim();
        if (trimmedCookie.startsWith(prefix)) {
            const [nameValuePair, ...rest] = trimmedCookie.split('=');
            const cookieName = nameValuePair;
            const cookieValue = rest.join('=');

            const key = cookieName.substring(prefix.length);
            cookies[key] = cookieValue;
        }
    }

    for (const cookieNameWithPrefix in cookies) {
        if (Object.prototype.hasOwnProperty.call(cookies, cookieNameWithPrefix)) {
            document.cookie = `${prefix}${cookieNameWithPrefix}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        }
    }

    return cookies;
};

const clearSearchParams = () => {
    if (window.history.replaceState) {
        const cleanURL = window.location.protocol + '//' + window.location.host + window.location.pathname;
        window.history.replaceState({ path: cleanURL }, '', cleanURL);
    } else {
        console.warn('History API is not supported in this browser.');
    }
};

export const getInitPlayerOptions = (): TInitPayerOptions => {
    const externalData = managePs3PlayerCookies();

    const urlObj = new URL(window.location.href);
    const params = new URLSearchParams(urlObj.search);
    if (params.size !== 0) {
        params.forEach((value, key) => {
            externalData[key] = value as never;
        });
        clearSearchParams();
    }
    const strRequestUri = sessionStorage.getItem(REQUEST_URI) as string;
    const result = {
        externalData,
        requestUri: strRequestUri ? (JSON.parse(strRequestUri) as TRequestUri) : undefined,
    } as TInitPayerOptions;
    sessionStorage.removeItem(REQUEST_URI);
    return result;
};

export function updateWidgetInWidgetsForTest(
    widgets: TPayloadWidget<TCommonData>[],
    updatedWidget: TPayloadWidget<TCommonData>,
): TPayloadWidget<TCommonData>[] {
    return updateWidgetInWidgets(widgets, updatedWidget);
}

export const updateSystemActions = (newWidget: TPayloadWidget<TCommonData>) => {
    const currentPageLS = localStorage.getItem(CURRENT_PAGE_WIDGETS) ?? '[]';
    const currentPageWidgets = JSON.parse(currentPageLS) as Array<TPayloadWidget<TCommonData>>;
    const newArrayOfWidgets = updateWidgetInWidgets(currentPageWidgets, newWidget);
    const systemActions = getWidgets(newArrayOfWidgets)?.systemActions;

    localStorage.setItem(CURRENT_PAGE_WIDGETS, JSON.stringify(newArrayOfWidgets));
    localStorage.setItem(SYSTEM_ACTIONS, JSON.stringify(systemActions));
};
export const initSystemActions = (currentPage: Array<TPayloadWidget<TCommonData>>, systemActions: TSystemActions) => {
    localStorage.setItem(CURRENT_PAGE_WIDGETS, JSON.stringify(currentPage));
    localStorage.setItem(SYSTEM_ACTIONS, JSON.stringify(systemActions));
};

export const getSystemAction = (systemActionType: ActionSystemActionType) => {
    const systemActionStr = localStorage.getItem(SYSTEM_ACTIONS);
    if (systemActionStr) {
        return (JSON.parse(systemActionStr) as TSystemActions)?.[systemActionType];
    }
    return null;
};
export const getWidgetDataOrUpdatePage = async <TD extends TCommonData>(
    action: TWAction,
    widgetUid: TWidget<TD>['uid'],
    internalId: TWInternalId,
    currentScenarioId: string,
    currentPageId: string,
    dispatch: AppDispatchType,
    isUpdateWidgetByUid = true,
    playerId?: string | undefined,
    isShowLoader?: boolean,
): Promise<TWidget<TD> | undefined> => {
    const json = await dispatch(
        fetchJsonAsync({
            action: action,
            widgetUid: widgetUid,
            isShowLoader: isShowLoader,
            playerId: playerId,
        }),
    ).unwrap();
    const jsonScenario = json?.scenario;
    if (
        internalId &&
        action.type === ActionTypeEnum.Widget &&
        jsonScenario?.scenarioId === currentScenarioId &&
        jsonScenario?.pages?.[0]?.id === currentPageId
    ) {
        const widgets = jsonScenario.pages[0].widgets;
        if (Array.isArray(widgets)) {
            const result = getWidgetByActionWidgetId(widgets, internalId);
            if (result.widget?.uid && isUpdateWidgetByUid) {
                dispatch(addMutableWidgets({ widgetUid, widgets: result.widget }));
            }
            if (result.bottomSheetMap) {
                dispatch(updateBottomSheetsWidgetsMap(result.bottomSheetMap));
            }
            if (result.modalWindowMap) {
                dispatch(updateModalWindowsMap(result.modalWindowMap));
            }
            if (result.internalIdWidgets) {
                dispatch(updateInternalIdWidgets(result.internalIdWidgets));
            }
            if (result.widget) {
                updateSystemActions(result.widget);
                return {
                    ...result.widget,
                    widgets: result.widget?.widgets?.map((widget) => widget?.uid),
                } as TWidget<TD>;
            }
        }
    } else {
        dispatch(updateScenario(json));
    }
    return Promise.resolve(undefined);
};
